import type { BrandFitTags } from "@cfg/brand-sdk";
import type { FitTagSuggestion } from "@cfg/configurator-ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useApp } from "./app-context.tsx";
import { useAuthUser } from "./auth.ts";

/** A ref's picture from the brand's template (`fitTags.imageUrl`), for when no lookup is at hand. */
export function fitImageUrl(fitTags: Pick<BrandFitTags, "imageUrl">, ref: string): string | undefined {
  return fitTags.imageUrl ? fitTags.imageUrl.replaceAll("{ref}", encodeURIComponent(ref)) : undefined;
}

/** The brand's fit directory (refs anyone tagged, most-used first), shared by every fit input. */
export function useFitDirectory() {
  const { api, brand } = useApp();
  return useQuery({ queryKey: ["fits", brand.config.id], queryFn: () => api!.listFits(), enabled: !!api && brand.config.features.fitTags, staleTime: 5 * 60_000 });
}

/**
 * Refs other users tagged (with names, and pictures from the brand's template)
 * plus the brand's external catalog search, which needs a signed-in user.
 */
export function useFitSources() {
  const { api, auth, brand } = useApp();
  const user = useAuthUser(auth);
  const fitTags = brand.config.fitTags;
  const known = useFitDirectory();
  const lookup = useMemo(() => (fitTags.lookup && api && user ? (q: string) => api.lookupFits(q).then((r) => r.results) : undefined), [fitTags.lookup, api, user]);
  const suggestions = useMemo(
    () => known.data?.fits.map((f): FitTagSuggestion => ({ ...f, imageUrl: fitImageUrl(fitTags, f.ref) })),
    [known.data, fitTags],
  );
  return { suggestions, lookup };
}

/**
 * What is known about one ref: its name (the fit directory, else `name`) and
 * picture and year (the external catalog when signed in, else the brand's
 * picture template).
 */
export function useFitInfo(ref: string | undefined, name?: string): FitTagSuggestion | undefined {
  const { api, auth, brand } = useApp();
  const user = useAuthUser(auth);
  const fitTags = brand.config.fitTags;
  const directory = useFitDirectory();
  const remote = useQuery({
    queryKey: ["fit-info", brand.config.id, ref],
    queryFn: async () => (await api!.lookupFits(ref!)).results.find((r) => r.ref === ref) ?? null,
    enabled: !!(ref && fitTags.lookup && api && user),
    staleTime: Infinity,
    retry: false,
  });
  if (!ref) return undefined;
  const r = remote.data ?? undefined;
  return {
    ref,
    name: directory.data?.fits.find((f) => f.ref === ref)?.name || name || r?.name,
    year: r?.year,
    imageUrl: r?.imageUrl ?? fitImageUrl(fitTags, ref),
  };
}
